import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Phone, ArrowRight, AlertTriangle, Shield, Scale, FileCheck, Building2, Factory } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import MethodologyCallout from '@/components/MethodologyCallout';
import Breadcrumbs from '@/components/Breadcrumbs';

const MEESCommercialBuildings = () => {
  const faqItems = [
    {
      question: 'What are the MEES regulations for commercial buildings?',
      answer: 'The Minimum Energy Efficiency Standards (MEES) regulations — set out in the Energy Efficiency (Private Rented Property) (England and Wales) Regulations 2015 — require that all non-domestic rental properties in England and Wales achieve a minimum EPC rating of E. Since 1 April 2023 this applies to all continuing commercial tenancies, not just new lettings. Landlords cannot legally let properties rated F or G without a valid exemption registered on the PRS Exemptions Register.'
    },
    {
      question: 'Is EPC C by 2027 still government policy?',
      answer: 'No. In its June 2026 interim response DESNZ confirmed the previously proposed 2027 EPC C interim milestone for non-domestic MEES will not be taken forward. The current Band E statutory minimum remains in force. The proposed future standard is EPC B by 2031 — but only for buildings over 1,000 m², where cost-effective, and only once secondary legislation passes Parliament. Buildings under 1,000 m² remain at Band E with no further deadline currently defined.'
    },
    {
      question: 'What is the 2031 EPC B proposal?',
      answer: 'Under the DESNZ June 2026 interim response, the government intends that from 2031 all privately rented non-domestic buildings over 1,000 m² in England and Wales will need to reach a minimum EPC B rating, where cost-effective. The existing 7-year payback exemption and other flexibility mechanisms will remain. Buildings under 1,000 m² are intended to continue at the current EPC E standard, with no further set deadline beyond that. The EPC B requirement is not yet law — it will only take effect following the successful passage of secondary legislation. Landlords should watch for a forthcoming full government response and updated guidance.'
    },
    {
      question: 'What are the penalties for MEES non-compliance?',
      answer: 'Financial penalties are set out in Regulation 39 of the Energy Efficiency (Private Rented Property) (England and Wales) Regulations 2015 and enforced by the Local Weights and Measures Authority. For a breach of the letting prohibition of less than 3 months, the penalty is 10% of the property\'s rateable value (minimum £5,000, maximum £50,000). For a breach of 3 months or more, the penalty rises to 20% of the rateable value (minimum £10,000, maximum £150,000). A publication penalty (notice of non-compliance on the PRS Exemptions Register) may also be issued.'
    },
    {
      question: 'Can I get an exemption from MEES?',
      answer: 'Yes. Non-domestic MEES exemptions (per the DESNZ landlord guidance) include: (1) the 7-year payback exemption — where the cost of a recommended measure or package is greater than the value of expected energy-bill savings over 7 years; (2) the "all improvements made" exemption — where all relevant improvements have been installed and the property still fails; (3) the wall insulation exemption — where a recognised expert confirms cavity, external or internal wall insulation would harm the fabric or structure; (4) the third-party consent exemption — where a required consent from a tenant, superior landlord, mortgagee, freeholder or planning department cannot reasonably be obtained; (5) the property devaluation exemption — where a RICS-registered valuer confirms the improvement would devalue the property by more than 5%; and (6) a 6-month temporary exemption where a person has recently become the landlord under specified circumstances. All exemptions must be registered on the PRS Exemptions Register before they can be relied on.'
    },
    {
      question: 'Do MEES regulations apply to warehouses and factories?',
      answer: 'Yes. Non-domestic MEES applies to all privately rented non-domestic property in England and Wales that is legally required to have an EPC and is let on a "term certain" between 6 months and 99 years — including warehouses, factories, workshops, distribution centres and light-industrial units. Industrial buildings are a common area of MEES exposure because single-skin steel cladding and older gas radiant heating typically produce lower SBEM ratings.'
    },
    {
      question: 'How can I improve my commercial property EPC rating to meet MEES?',
      answer: 'Common cost-effective improvements for commercial and industrial buildings include upgrading to LED lighting with modern controls, adding roof or cavity wall insulation, replacing inefficient heating and hot-water plant (including air-source heat pumps), improving heating controls and zone management, sealing air-leakage paths, and installing solar PV. Our Non-Domestic Energy Advice Report (NDEAR) ranks all applicable measures against the statutory 7-year payback test so you can distinguish measures inside and outside the statutory obligation.'
    },
    {
      question: 'How long do MEES exemptions last?',
      answer: 'Most non-domestic MEES exemptions are valid for 5 years from the date of registration on the PRS Exemptions Register. After 5 years the exemption expires and the landlord must either improve the property to at least Band E or register a new exemption if the qualifying conditions still apply. The 6-month temporary exemption for a person who has recently become the landlord is the notable exception. Exemptions do not automatically transfer to a new owner on sale — the new owner must either bring the property up to standard at that point or register a valid exemption in their own name.'
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "MEES Regulations for Commercial Buildings: A Landlord's Guide",
    "author": {
      "@type": "Organization",
      "name": "Energy Performance Direct"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Energy Performance Direct",
      "url": "https://energyperformancedirect.co.uk"
    },
    "description": "Complete guide to Minimum Energy Efficiency Standards (MEES) for commercial and industrial buildings, including compliance requirements, penalties and exemptions."
  };

  return (
    <>
      <Helmet>
        <title>MEES Regulations for Commercial Buildings | Minimum Energy Efficiency Standards Guide</title>
        <meta name="description" content="Complete guide to MEES regulations for commercial and industrial buildings. Understand minimum energy efficiency standards, compliance requirements, penalties and exemptions for landlords." />
        <meta name="keywords" content="MEES advice, MEES compliance advice, commercial MEES compliance, industrial MEES compliance, warehouse MEES advice, factory MEES advice, minimum energy efficiency standards advice, commercial property MEES advice, industrial property MEES advice, commercial EPC compliance advice, industrial EPC compliance advice" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/mees-commercial-buildings" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <Navbar />
      <Breadcrumbs items={[{ label: 'Resources', path: '/resources' }, { label: 'MEES for Commercial Buildings', path: '/mees-commercial-buildings' }]} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-red-900 via-red-800 to-red-900 text-white py-16" data-testid="mees-hero">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-red-500/20 text-red-200 px-4 py-2 rounded-full text-sm mb-6">
              <Shield className="w-4 h-4" />
              Compliance Guide
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              MEES Regulations for Commercial and Industrial Buildings
            </h1>
            <p className="text-lg text-red-200 mb-8 max-w-3xl mx-auto">
              What every landlord of warehouses, factories, offices and commercial premises needs to know about Minimum Energy Efficiency Standards compliance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-green-600 hover:bg-green-700">
                <Link to="/booking?service=commercial_warehouse">
                  Get Compliance Assessment
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <a href="tel:01392980077">
                  <Phone className="mr-2 w-5 h-5" />
                  Call 01392 980077
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What are MEES */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What Are the MEES Regulations?</h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p>
                The Minimum Energy Efficiency Standards (MEES) were introduced under the Energy Efficiency (Private Rented Property) (England and Wales) Regulations 2015. They set a minimum energy performance standard for privately rented non-domestic properties in England and Wales, applying to all buildings let under a lease or tenancy agreement.
              </p>
              <p>
                Since 1 April 2023, all non-domestic rental properties must achieve a minimum EPC rating of E. This applies to both new lettings and existing tenancies. Landlords of warehouses, factories, workshops, offices, retail units, hotels and all other commercial premises must ensure their properties meet this standard or face significant financial penalties.
              </p>
              <p>
                The regulations were introduced as part of the UK government's strategy to reduce carbon emissions from the building stock. Non-domestic buildings account for a substantial proportion of the UK's total energy consumption, and many older industrial and commercial properties have poor energy performance due to minimal insulation, inefficient heating systems and outdated lighting.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Dates Timeline */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">MEES Timeline for Commercial Properties</h2>
            <div className="space-y-6">
              {[
                { date: 'April 2018', title: 'New Lettings', desc: 'MEES first applied to new lettings and lease renewals under the Energy Efficiency (Private Rented Property) (England and Wales) Regulations 2015. Landlords could no longer grant new leases on properties rated below EPC E.', status: 'past' },
                { date: 'April 2023', title: 'All Tenancies', desc: 'MEES extended to all continuing tenancies. All non-domestic private-rented properties in England and Wales must meet at least EPC E unless a valid exemption is registered.', status: 'current' },
                { date: 'June 2026', title: 'DESNZ Interim Response', desc: 'DESNZ published its interim response to the 2019 and 2021 non-domestic MEES consultations. Confirmed direction: the previously proposed 2027 EPC C interim milestone will not be taken forward; a proposed EPC B by 2031 will apply to buildings over 1,000 m² only, where cost-effective. Buildings under 1,000 m² remain at EPC E with no further deadline currently defined.', status: 'current' },
                { date: 'Proposed 2031', title: 'EPC B — Buildings over 1,000 m²', desc: 'Under the DESNZ interim response, private-rented non-domestic buildings over 1,000 m² are proposed to reach EPC B by 2031, where cost-effective. This will only take effect following the successful passage of secondary legislation through Parliament — it is not yet law. A full government response and updated guidance are pending.', status: 'future' }
              ].map((item, i) => (
                <div key={i} className="flex gap-6 items-start">
                  <div className={`flex-shrink-0 w-4 h-4 rounded-full mt-2 ${item.status === 'past' ? 'bg-gray-400' : item.status === 'current' ? 'bg-red-600' : 'bg-amber-500'}`} />
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <span className={`text-sm font-semibold ${item.status === 'current' ? 'text-red-600' : 'text-gray-500'}`}>{item.date}</span>
                      {item.status === 'current' && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Now in Force</span>}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-gray-700">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Penalties */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Penalties for Non-Compliance</h2>
            <div className="prose prose-lg max-w-none text-gray-700 mb-8">
              <p>
                The Regulations are enforced by the Local Weights and Measures Authority. Financial penalties are set out in Regulation 39 of the Energy Efficiency (Private Rented Property) (England and Wales) Regulations 2015 and are calculated by reference to the property&apos;s rateable value and the duration of the breach:
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-red-200">
                <CardContent className="p-6">
                  <AlertTriangle className="w-8 h-8 text-red-600 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Breach Under 3 Months</h3>
                  <p className="text-gray-700 text-sm mb-3">10% of rateable value</p>
                  <p className="text-sm text-gray-500">Minimum: £5,000 | Maximum: £50,000</p>
                </CardContent>
              </Card>
              <Card className="border-red-200">
                <CardContent className="p-6">
                  <AlertTriangle className="w-8 h-8 text-red-600 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Breach Over 3 Months</h3>
                  <p className="text-gray-700 text-sm mb-3">20% of rateable value</p>
                  <p className="text-sm text-gray-500">Minimum: £10,000 | Maximum: £150,000</p>
                </CardContent>
              </Card>
            </div>
            <div className="mt-8 prose prose-lg max-w-none text-gray-700">
              <p>
                Alongside the financial penalty, the enforcement authority may publish a notice of non-compliance on the PRS Exemptions Register, which is publicly searchable and increasingly checked by institutional tenants, commercial lenders and insurers as part of due diligence. Non-compliance can therefore also affect mortgageability, marketability and rent negotiations.
              </p>
              <p className="text-sm text-gray-600 mt-4">
                <strong>Primary sources:</strong>{' '}
                <a href="https://www.gov.uk/guidance/non-domestic-private-rented-property-minimum-energy-efficiency-standard-landlord-guidance" target="_blank" rel="noopener noreferrer" className="text-green-700 underline">DESNZ non-domestic landlord guidance</a> (last updated May 2026);{' '}
                <a href="https://www.gov.uk/government/consultations/non-domestic-private-rented-sector-minimum-energy-efficiency-standards-epc-b-implementation/outcome/minimum-energy-efficiency-standards-mees-in-the-non-domestic-private-rented-sector-interim-response" target="_blank" rel="noopener noreferrer" className="text-green-700 underline">DESNZ interim response on non-domestic MEES EPC B implementation</a> (June 2026); Energy Efficiency (Private Rented Property) (England and Wales) Regulations 2015. This page is a guide, not legal advice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MEES for Industrial */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">MEES Compliance for Industrial Buildings</h2>
            <div className="prose prose-lg prose-invert max-w-none">
              <p>
                Industrial buildings frequently present MEES compliance challenges. Many warehouses, factories and workshops in Devon, Cornwall, Somerset and Dorset were constructed before modern insulation and energy efficiency standards existed. Older steel-frame buildings with single-skin cladding, uninsulated roofs and outdated heating systems commonly rate E, F or G on the EPC scale.
              </p>
              <p>
                If your industrial property currently rates F or G, you must either improve it to achieve at least an E rating or register a valid exemption before letting it. Our <Link to="/industrial-epc" className="text-green-400 hover:underline">industrial EPC assessments</Link> include detailed improvement recommendations showing which measures will achieve MEES compliance most cost-effectively.
              </p>
              <p>
                Common routes to MEES compliance for industrial buildings include:
              </p>
              <ul>
                <li>Upgrading to LED high-bay lighting (often the single most impactful measure)</li>
                <li>Adding roof insulation above existing profiled sheet cladding</li>
                <li>Replacing old radiant heaters with modern condensing units</li>
                <li>Installing heating controls where none currently exist</li>
                <li>Sealing air leakage around roller shutter doors and service penetrations</li>
                <li>Installing solar PV panels on large industrial roof areas</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Exemptions */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">MEES Exemptions</h2>
            <div className="prose prose-lg max-w-none text-gray-700 mb-8">
              <p>
                Where a commercial property cannot cost-effectively be improved to an E rating, landlords may qualify for an exemption. All exemptions must be registered on the PRS Exemptions Register and are valid for 5 years.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: Scale, title: '7-Year Payback Exemption', desc: 'All energy efficiency improvements with a simple payback of 7 years or less have been carried out, but the property still rates below E. This is the most commonly used exemption for industrial buildings.' },
                { icon: FileCheck, title: 'Consent Exemption', desc: 'A third party whose consent is required for improvements (such as a tenant, planning authority or mortgage lender) has refused permission despite reasonable efforts to obtain it.' },
                { icon: Building2, title: 'Devaluation Exemption', desc: 'An independent RICS surveyor has provided a report stating that the required improvements would reduce the market value of the property by more than 5%.' },
                { icon: Factory, title: 'New Landlord Exemption', desc: 'A temporary 6-month exemption for landlords who have recently become the landlord of a non-compliant property (for example, through inheritance).' }
              ].map((item, i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <item.icon className="w-8 h-8 text-blue-600 mb-3" />
                    <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">MEES Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqItems.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-sm" data-testid={`mees-faq-${index}`}>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Guides and Services</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link to="/industrial-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Industrial EPCs</h3>
                <p className="text-sm text-gray-600">Warehouse, factory and workshop assessments</p>
              </Link>
              <Link to="/sbem-explained" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">SBEM Explained</h3>
                <p className="text-sm text-gray-600">How commercial EPC ratings are calculated</p>
              </Link>
              <Link to="/warehouse-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Warehouse EPCs</h3>
                <p className="text-sm text-gray-600">Distribution centres and storage facilities</p>
              </Link>
              <Link to="/factory-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Factory EPCs</h3>
                <p className="text-sm text-gray-600">Manufacturing plants and production facilities</p>
              </Link>
              <Link to="/industrial-epc-devon" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Industrial EPCs Devon</h3>
                <p className="text-sm text-gray-600">Exeter, Plymouth, Barnstaple assessments</p>
              </Link>
              <Link to="/resources/epc-explained" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">EPC Explained</h3>
                <p className="text-sm text-gray-600">General guide to Energy Performance Certificates</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Need MEES Compliance Advice?</h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Our assessors provide practical guidance for commercial and industrial landlords across Devon, Cornwall, Somerset and Dorset. Get your property assessed and receive tailored improvement recommendations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-green-600 hover:bg-gray-100">
              <Link to="/booking?service=commercial_warehouse">
                Book Assessment
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <a href="tel:01392980077">
                <Phone className="mr-2 w-5 h-5" />
                Call 01392 980077
              </a>
            </Button>
          </div>
        </div>
      </section>

      <MethodologyCallout
        context="For every commercial MEES advice engagement,"
        reportCode="NDEAR (Non-Domestic Energy Advice Report)"
      />

      <Footer />
    </>
  );
};

export default MEESCommercialBuildings;
